import React, { useState, useRef, useEffect } from "react";
import { updateDoc, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase-config";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
  TableHead,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  Divider,
  Paper,
  Grid,
  InputAdornment,
  alpha,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  CircularProgress
} from "@mui/material";
import { Edit as EditIcon, Check as CheckIcon, Email as EmailIcon } from "@mui/icons-material";
import html2pdf from "html2pdf.js";
import LOGO from "../assets/MF-CPU-LOGO.png";
import { getFunctions, httpsCallable } from "firebase/functions";

// Custom theme for MF application
const mfTheme = {
  colors: {
    primary: '#C4161C', // App theme red
    secondary: '#F4B400', // Gold accent
    accent: '#059669', // Green for success
    warning: '#d97706', // Amber for warnings
    success: '#10b981', // Green
    textPrimary: '#1f2937',
    textSecondary: '#6b7280',
    surface: '#ffffff',
    surfaceLuxury: '#f8fafc',
    borderPrimary: '#e5e7eb',
    surfaceElevated: '#f9fafb'
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px'
  },
  borderRadius: {
    small: '4px',
    medium: '8px',
    large: '12px',
    rounded: '16px'
  },
  gradients: {
    hero: 'linear-gradient(135deg, #C4161C 0%, #E53935 100%)'
  },
  typography: {
    fontWeights: {
      normal: 400,
      medium: 500,
      semiBold: 600,
      bold: 700
    }
  },
  shadows: {
    soft: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
  }
};

const InvoiceModal = ({ invoice, isModalOpen, handleClose }) => {
  // ✅ ADD RESPONSIVE HOOKS
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [editableInvoice, setEditableInvoice] = useState(invoice);
  const [editingIndex, setEditingIndex] = useState(null); // Index of the item being edited
  const [inputValue, setInputValue] = useState(""); // For editing price and quantity
  const [editField, setEditField] = useState(null); // Field being edited (price, quantity, etc.)
  const [showDropdown, setShowDropdown] = useState(false);
  const invoiceRef = useRef();

  // 💰 DISCOUNT STATES
  const [discountType, setDiscountType] = useState('none');
  const [discountValue, setDiscountValue] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [originalAmount, setOriginalAmount] = useState(0);

  // 📧 EMAIL STATES
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState({ show: false, message: "", type: "success" });

  const functions = getFunctions();

  // Load restaurant email on open
  useEffect(() => {
    const fetchEmail = async () => {
      if (isModalOpen && invoice?.userId) {
        try {
          const userDoc = await getDoc(doc(db, "users", invoice.userId));
          if (userDoc.exists()) {
            setRecipientEmail(userDoc.data().email || "");
          }
        } catch (error) {
          console.error("Error fetching restaurant email:", error);
        }
      }
    };
    fetchEmail();
  }, [isModalOpen, invoice?.userId]);

  useEffect(() => {
    setEditableInvoice(invoice);
    setDiscountType(invoice.discountType || 'none');
    setDiscountValue(invoice.discountValue || '');
    setDiscountReason(invoice.discountReason || '');
    setOriginalAmount(invoice.totalPrice || 0);
  }, [invoice]);

  // 💰 DISCOUNT CALCULATION
  const calculateDiscountedTotal = (originalTotal = 0) => {
    if (discountType === 'none' || !discountValue || originalTotal === 0) {
      return originalTotal;
    }
    const discount = parseFloat(discountValue) || 0;
    if (discountType === 'percent') {
      return originalTotal * (1 - discount / 100);
    }
    return originalTotal - discount;
  };

  const safeToFixed = (value, decimals = 2) => {
    if (value === undefined || value === null || isNaN(value)) return '0.00';
    return Number(value).toFixed(decimals);
  };

  const safeNumber = (value) => {
    if (value === undefined || value === null || isNaN(value)) return 0;
    return Number(value);
  };

  const handleStatusChange = (field, value) => {
    setEditableInvoice((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateTotalPrice = () => {
    const totalPrice = editableInvoice.items.reduce((total, item) => {
      return total + item.price * item.quantity;
    }, 0);
    setEditableInvoice((prev) => ({
      ...prev,
      totalPrice,
    }));
  };

  // ✅ ENHANCED EDIT FUNCTIONALITY
  const handleEditField = (field, index, value) => {
    const newItems = [...editableInvoice.items];

    // Handle numeric fields
    if (field === 'price' || field === 'quantity') {
      newItems[index][field] = safeNumber(value);
    } else {
      newItems[index][field] = value;
    }

    setEditableInvoice((prev) => ({
      ...prev,
      items: newItems,
    }));

    // Recalculate totals
    const totalPrice = newItems.reduce((total, item) => {
      return total + (item.price || 0) * (item.quantity || 0);
    }, 0);

    setEditableInvoice((prev) => ({
      ...prev,
      totalPrice,
    }));
  };

  const handleBlur = () => {
    if (editingIndex !== null) {
      let processedValue = inputValue;

      // Convert to number for price and quantity fields
      if (editField === 'price' || editField === 'quantity') {
        processedValue = safeNumber(inputValue);
      }

      handleEditField(editField, editingIndex, processedValue);
      setEditingIndex(null);
      setEditField(null);
      setInputValue("");
    }
  };

  const handleSave = async () => {
    const invoiceDocRef = doc(db, "invoices", editableInvoice.id);
    try {
      await updateDoc(invoiceDocRef, {
        ...editableInvoice,
        // ✅ SAVE DISCOUNT FIELDS
        discountType,
        discountValue: discountType !== 'none' ? parseFloat(discountValue) || 0 : null,
        discountReason: discountType !== 'none' ? discountReason : null,
        discountedTotal: calculateDiscountedTotal(originalAmount)
      });
      alert("Invoice updated successfully!");
      handleClose();
    } catch (err) {
      console.error("Error updating invoice: ", err);
    }
  };

  // ✅ ENHANCED PDF GENERATION
  const handleSendInvoice = () => {
    setTimeout(() => {
      const element = invoiceRef.current;
      const options = {
        margin: [0.5, 0.5, 0.5, 0.5],
        filename: `Invoice_${editableInvoice.id}.pdf`,
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          scrollX: 0,
          scrollY: 0,
          windowWidth: 1024,
          windowHeight: 768
        },
        jsPDF: {
          unit: "in",
          format: "letter",
          orientation: "portrait",
          compress: true
        }
      };

      const clone = element.cloneNode(true);

      // Remove edit icons
      const editIcons = clone.querySelectorAll(".edit-icon");
      editIcons.forEach((icon) => (icon.style.display = "none"));

      // Remove discount section from PDF
      const discountBlocks = clone.querySelectorAll('.hide-in-pdf');
      discountBlocks.forEach(block => block.remove());

      // Replace MUI Select components (rendered as divs) with plain text
      const pdfStatusSections = clone.querySelectorAll('.pdf-status-section');
      pdfStatusSections.forEach((section) => {
        const label = section.getAttribute('data-label') || '';
        const value = section.getAttribute('data-value') || '';
        const span = document.createElement('div');
        span.style.fontSize = '14px';
        span.style.fontWeight = '500';
        span.style.marginBottom = '8px';
        span.innerHTML = `<strong>${label}:</strong> ${value}`;
        section.parentNode.replaceChild(span, section);
      });

      // Replace native dropdowns with text
      const dropdowns = clone.querySelectorAll("select");
      dropdowns.forEach((dropdown) => {
        const selectedValue = dropdown.options[dropdown.selectedIndex].text;
        const span = document.createElement("span");
        span.textContent = selectedValue;
        span.style.fontSize = "14px";
        span.style.fontWeight = "500";
        dropdown.parentNode.replaceChild(span, dropdown);
      });

      // Optimize table layout for PDF
      const tableRows = clone.querySelectorAll("tr");
      tableRows.forEach((row) => {
        row.style.height = "auto";
        row.style.minHeight = "32px";
        row.style.lineHeight = "1.2";
      });

      const tableCells = clone.querySelectorAll("td, th");
      tableCells.forEach((cell) => {
        cell.style.padding = "6px 8px";
        cell.style.fontSize = "12px";
        cell.style.lineHeight = "1.3";
        cell.style.verticalAlign = "middle";
      });

      // Fix word spacing in all text elements
      const allElements = clone.querySelectorAll('*');
      allElements.forEach((el) => {
        el.style.wordSpacing = 'normal';
        el.style.letterSpacing = 'normal';
        el.style.whiteSpace = 'normal';
      });

      // Add custom CSS for PDF
      const style = document.createElement('style');
      style.textContent = `
        * {
          word-spacing: normal !important;
          letter-spacing: normal !important;
          -webkit-font-smoothing: antialiased;
        }
        @media print {
          .hide-in-pdf { display: none !important; }
          table { page-break-inside: auto !important; }
          tbody tr { page-break-inside: avoid !important; }
        }
      `;
      clone.appendChild(style);

      html2pdf().from(clone).set(options).save();
    }, 100);
  };

  // 📧 SEND EMAIL WITH PDF ATTACHMENT
  const handleSendEmail = async () => {
    if (!recipientEmail) return;
    setIsSendingEmail(true);

    try {
      const element = invoiceRef.current;
      if (!element) return;

      // --- GENERATE PDF BASE64 (Same logic as handleSendInvoice) ---
      const options = {
        margin: [0.5, 0.5, 0.5, 0.5],
        html2canvas: {
          scale: 2,
          useCORS: true,
          windowWidth: 1024
        },
        jsPDF: {
          unit: "in",
          format: "letter",
          orientation: "portrait"
        }
      };

      const clone = element.cloneNode(true);
      clone.querySelectorAll(".edit-icon").forEach(el => el.style.display = "none");
      clone.querySelectorAll('.hide-in-pdf').forEach(el => el.remove());

      const pdfDataUri = await html2pdf().from(clone).set(options).outputPdf('datauristring');
      const pdfBase64 = pdfDataUri.split(',')[1];
      // -------------------------------------------------------------

      // Build specific HTML body for the email
      const invoiceDate = new Date(editableInvoice.createdAt?.toDate?.() || editableInvoice.createdAt).toLocaleDateString('en-GB');
      const totalAmount = safeToFixed(calculateDiscountedTotal(editableInvoice.totalPrice));

      const htmlBody = `
      <div style="font-family:Arial,sans-serif;color:#333;max-width:600px;margin:0 auto;border:1px solid #eee;border-radius:10px;overflow:hidden">
        <div style="background:#C4161C;color:white;padding:20px;text-align:center">
          <h1 style="margin:0">Invoice Attachment</h1>
        </div>
        <div style="padding:20px">
          <p>Hello,</p>
          <p>Please find the attached invoice for <strong>${editableInvoice.restaurantName || 'your restaurant'}</strong>.</p>
          <div style="background:#f9f9f9;padding:15px;border-radius:5px;margin:20px 0">
            <p style="margin:5px 0"><strong>Invoice Date:</strong> ${invoiceDate}</p>
            <p style="margin:5px 0"><strong>Total Amount:</strong> £${totalAmount}</p>
          </div>
          <p>If you have any questions, please contact us.</p>
        </div>
        <div style="background:#f5f5f5;padding:15px;text-align:center;font-size:12px;color:#888">
          Sent via Madras Flavours Kitchen Limited System
        </div>
      </div>
    `;

      const sendEmailFn = httpsCallable(functions, 'manualSendInvoiceEmail');
      await sendEmailFn({
        recipientEmail,
        subject: `📧 Invoice - ${editableInvoice.restaurantName || ''} - ${invoiceDate}`,
        htmlBody: htmlBody,
        pdfBase64: pdfBase64,
        fileName: `Invoice_${editableInvoice.id}.pdf`,
        restaurantId: editableInvoice.userId || null,
        restaurantName: editableInvoice.restaurantName || null,
        invoiceDate: invoiceDate,
      });

      setEmailStatus({
        show: true,
        message: "Email sent successfully!",
        type: "success"
      });
      setEmailDialogOpen(false);
    } catch (error) {
      console.error("Error sending email:", error);
      setEmailStatus({
        show: true,
        message: `Failed to send email: ${error.message}`,
        type: "error"
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  // ✅ ENHANCED EDITABLE FIELD COMPONENT
  const EditableField = ({
    value,
    field,
    index,
    type = 'text',
    options = null,
    displayValue = null
  }) => {
    const isEditing = editingIndex === index && editField === field;

    if (isEditing) {
      if (type === 'select') {
        return (
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <Select
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onBlur={handleBlur}
              autoFocus
              sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      } else {
        return (
          <TextField
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onBlur={handleBlur}
            onKeyPress={(e) => e.key === 'Enter' && handleBlur()}
            type={type}
            size="small"
            variant="outlined"
            autoFocus
            sx={{
              width: isMobile ? (type === 'number' ? '80px' : '120px') : 'auto',
              '& .MuiInputBase-input': {
                fontSize: isMobile ? '0.75rem' : '0.875rem'
              }
            }}
          />
        );
      }
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{
          fontSize: isMobile ? '0.75rem' : '0.875rem'
        }}>
          {displayValue || value}
        </Typography>
        <IconButton
          size="small"
          className="edit-icon"
          onClick={() => {
            setEditingIndex(index);
            setEditField(field);
            setInputValue(String(value || ''));
          }}
          sx={{ padding: isMobile ? '2px' : '4px' }}
        >
          <EditIcon sx={{ fontSize: isMobile ? '14px' : '16px' }} />
        </IconButton>
      </Box>
    );
  };

  return (
    <Dialog
      open={isModalOpen}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : mfTheme.borderRadius.rounded,
          maxHeight: isMobile ? '100vh' : '90vh',
          margin: isMobile ? 0 : undefined,
          width: isMobile ? '100vw' : undefined,
        }
      }}
      scroll="paper"
    >
      {/* Enhanced Dialog Header */}
      <DialogTitle sx={{
        background: mfTheme.gradients.hero,
        color: "#FFFFFF",
        fontWeight: mfTheme.typography.fontWeights.bold,
        padding: isMobile ? theme.spacing(2) : theme.spacing(3),
        fontSize: isMobile ? '1.1rem' : '1.25rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span>📄 Invoice Details</span>
        <img
          src={LOGO}
          alt="Company Logo"
          style={{ width: isMobile ? "80px" : "100px" }}
        />
      </DialogTitle>

      <DialogContent sx={{
        padding: 0,
        overflowX: isMobile ? 'hidden' : 'auto'
      }}>
        <Box
          ref={invoiceRef}
          sx={{
            padding: isMobile ? theme.spacing(1.5) : mfTheme.spacing.lg,
            width: '100%',
          }}
        >
          {/* Invoice Header */}
          <Box sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            marginBottom: mfTheme.spacing.xl,
            padding: mfTheme.spacing.lg,
            backgroundColor: mfTheme.colors.surfaceLuxury,
            borderRadius: mfTheme.borderRadius.medium,
            gap: isMobile ? theme.spacing(2) : 0
          }}>
            <Box>
              <Typography variant="h6" sx={{
                fontWeight: 700,
                color: mfTheme.colors.primary,
                fontSize: isMobile ? '1rem' : '1.25rem'
              }}>
                Bill From:
              </Typography>
              <Typography variant="body1" sx={{
                fontWeight: 600,
                fontSize: isMobile ? '0.875rem' : '1rem'
              }}>
                Madras Flavours Kitchen Limited
              </Typography>
              <Typography variant="body2" sx={{
                color: mfTheme.colors.textSecondary,
                fontSize: isMobile ? '0.75rem' : '0.875rem'
              }}>
                122 High Street, Hounslow, TW3 1NA
              </Typography>
            </Box>
            <Box sx={{ textAlign: isMobile ? 'left' : 'right' }}>
              <Typography variant="h6" sx={{
                fontWeight: 700,
                color: mfTheme.colors.primary,
                fontSize: isMobile ? '1rem' : '1.25rem'
              }}>
                Invoice Date:
              </Typography>
              <Typography variant="body1" sx={{
                fontSize: isMobile ? '0.875rem' : '1rem'
              }}>
                {new Date(invoice.createdAt).toLocaleDateString('en-CA')}
              </Typography>
            </Box>
          </Box>

          {/* Customer Details */}
          <Box sx={{
            marginBottom: mfTheme.spacing.xl,
            padding: mfTheme.spacing.lg,
            backgroundColor: mfTheme.colors.surface,
            borderRadius: mfTheme.borderRadius.medium,
            border: `1px solid ${mfTheme.colors.borderPrimary}`
          }}>
            <Typography variant="h6" sx={{
              fontWeight: 700,
              color: mfTheme.colors.primary,
              marginBottom: 2,
              fontSize: isMobile ? '1rem' : '1.25rem'
            }}>
              Bill To:
            </Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: isMobile ? 1 : 2
            }}>
              <Box>
                <Typography variant="body2" sx={{
                  fontWeight: 600,
                  fontSize: isMobile ? '0.75rem' : '0.875rem'
                }}>Name:</Typography>
                <Typography variant="body1" sx={{
                  fontSize: isMobile ? '0.875rem' : '1rem'
                }}>{editableInvoice.name}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{
                  fontWeight: 600,
                  fontSize: isMobile ? '0.75rem' : '0.875rem'
                }}>Restaurant Name:</Typography>
                <Typography variant="body1" sx={{
                  fontSize: isMobile ? '0.875rem' : '1rem'
                }}>{editableInvoice.restaurantName}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{
                  fontWeight: 600,
                  fontSize: isMobile ? '0.75rem' : '0.875rem'
                }}>Address:</Typography>
                <Typography variant="body1" sx={{
                  fontSize: isMobile ? '0.875rem' : '1rem'
                }}>{editableInvoice.address}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{
                  fontWeight: 600,
                  fontSize: isMobile ? '0.75rem' : '0.875rem'
                }}>Phone:</Typography>
                <Typography variant="body1" sx={{
                  fontSize: isMobile ? '0.875rem' : '1rem'
                }}>{editableInvoice.phone}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{
                  fontWeight: 600,
                  fontSize: isMobile ? '0.75rem' : '0.875rem'
                }}>Email:</Typography>
                <Typography variant="body1" sx={{
                  fontSize: isMobile ? '0.875rem' : '1rem'
                }}>{editableInvoice.email}</Typography>
              </Box>
            </Box>
          </Box>

          {/* Enhanced Items Table */}
          <TableContainer
            component={Paper}
            sx={{
              marginBottom: mfTheme.spacing.xl,
              borderRadius: mfTheme.borderRadius.medium,
              boxShadow: mfTheme.shadows.soft,
              overflowX: isMobile ? 'auto' : 'visible'
            }}
          >
            <Table sx={{ minWidth: isMobile ? 800 : 'auto' }}>
              <TableHead sx={{ backgroundColor: mfTheme.colors.primary }}>
                <TableRow>
                  <TableCell sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    padding: isMobile ? '8px' : '16px'
                  }}>Title</TableCell>
                  <TableCell sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    padding: isMobile ? '8px' : '16px'
                  }}>Brand</TableCell>
                  <TableCell sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    padding: isMobile ? '8px' : '16px'
                  }}>Price (£/unit)</TableCell>
                  <TableCell sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    padding: isMobile ? '8px' : '16px'
                  }}>Quantity</TableCell>
                  <TableCell sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    padding: isMobile ? '8px' : '16px'
                  }}>Units</TableCell>
                  <TableCell sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    padding: isMobile ? '8px' : '16px'
                  }}>Total (£)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {editableInvoice.items.map((item, index) => (
                  <TableRow key={index} sx={{
                    "&:hover": {
                      backgroundColor: alpha(mfTheme.colors.primary, 0.05)
                    }
                  }}>
                    {/* Title */}
                    <TableCell sx={{ padding: isMobile ? '8px' : '16px' }}>
                      <EditableField
                        value={item.title}
                        field="title"
                        index={index}
                        type="text"
                      />
                    </TableCell>

                    {/* Brand */}
                    <TableCell sx={{ padding: isMobile ? '8px' : '16px' }}>
                      <EditableField
                        value={item.brand}
                        field="brand"
                        index={index}
                        type="text"
                      />
                    </TableCell>

                    {/* Price */}
                    <TableCell sx={{ padding: isMobile ? '8px' : '16px' }}>
                      <EditableField
                        value={item.price}
                        field="price"
                        index={index}
                        type="number"
                        displayValue={`£${safeToFixed(item.price)}`}
                      />
                    </TableCell>

                    {/* Quantity */}
                    <TableCell sx={{ padding: isMobile ? '8px' : '16px' }}>
                      <EditableField
                        value={item.quantity}
                        field="quantity"
                        index={index}
                        type="number"
                      />
                    </TableCell>

                    {/* Units */}
                    <TableCell sx={{ padding: isMobile ? '8px' : '16px' }}>
                      <EditableField
                        value={item.units}
                        field="units"
                        index={index}
                        type="select"
                        options={[
                          { value: 'pcs', label: 'Pieces' },
                          { value: 'kg', label: 'Kilograms' },
                          { value: 'g', label: 'Grams' },
                          { value: 'litres', label: 'Litres' },
                          { value: 'ml', label: 'Millilitres' },
                          { value: 'box', label: 'Box' },
                          { value: 'pack', label: 'Pack' }
                        ]}
                      />
                    </TableCell>

                    {/* Item Total */}
                    <TableCell sx={{ padding: isMobile ? '8px' : '16px' }}>
                      <Typography variant="body2" sx={{
                        fontWeight: 700,
                        color: mfTheme.colors.primary,
                        fontSize: isMobile ? '0.75rem' : '0.875rem'
                      }}>
                        £{safeToFixed((item.price || 0) * (item.quantity || 0))}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* 💰 DISCOUNT SECTION */}
          {editableInvoice?.totalPrice && (
            <Box className="hide-in-pdf" sx={{
              marginTop: 3,
              padding: 2,
              backgroundColor: alpha(mfTheme.colors.warning, 0.05),
              borderRadius: mfTheme.borderRadius.medium,
              pageBreakInside: 'avoid',
              breakInside: 'avoid'
            }}>
              <Typography variant="h6" sx={{
                color: mfTheme.colors.warning,
                mb: 2,
                fontWeight: 600,
                fontSize: isMobile ? '1rem' : '1.125rem'
              }}>
                💰 Discount (Optional)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Discount Type</InputLabel>
                    <Select
                      value={discountType}
                      label="Discount Type"
                      onChange={(e) => {
                        setDiscountType(e.target.value);
                        if (e.target.value === 'none') setDiscountValue('');
                      }}
                    >
                      <MenuItem value="none">No Discount</MenuItem>
                      <MenuItem value="percent">Percentage (%)</MenuItem>
                      <MenuItem value="amount">Fixed Amount (£)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={discountType === 'percent' ? 'Discount %' : 'Discount Amount (£)'}
                    type="number"
                    size="small"
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    disabled={discountType === 'none'}
                    InputProps={{
                      startAdornment: discountType === 'percent' ?
                        <InputAdornment position="start">%</InputAdornment> : null,
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Reason (Optional)"
                    size="small"
                    value={discountReason}
                    onChange={(e) => setDiscountReason(e.target.value)}
                    placeholder="e.g. Loyalty discount, First order promo"
                  />
                </Grid>

                {discountType !== 'none' && discountValue && (
                  <Grid item xs={12}>
                    <Box sx={{
                      p: 2,
                      backgroundColor: alpha(mfTheme.colors.success, 0.1),
                      borderRadius: mfTheme.borderRadius.small
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Original: £{safeToFixed(originalAmount)}
                      </Typography>
                      <Typography variant="h6" sx={{
                        color: mfTheme.colors.success,
                        fontWeight: 700
                      }}>
                        Discounted Total: £{safeToFixed(calculateDiscountedTotal(originalAmount))}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Reason: {discountReason}
                      </Typography>
                      <Typography variant="caption" sx={{ color: mfTheme.colors.textSecondary }}>
                        {discountType === 'percent' ? `${discountValue}%` : `£${discountValue}`} discount
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {/* Status Controls and Totals Section */}
          <Box sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'stretch' : 'flex-start',
            marginTop: mfTheme.spacing.xl,
            gap: isMobile ? mfTheme.spacing.lg : 0,
            pageBreakInside: 'avoid',
            breakInside: 'avoid'
          }}>
            {/* Status Controls */}
            <Box sx={{ flex: 1, order: isMobile ? 2 : 1 }}>
              {!showDropdown ? (
                <Box sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: 2
                }}>
                  <Box
                    className="pdf-status-section"
                    data-label="Order Status"
                    data-value={editableInvoice.orderStatus}
                  >
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel sx={{
                        fontSize: isMobile ? '0.75rem' : '0.875rem'
                      }}>Order Status</InputLabel>
                      <Select
                        value={editableInvoice.orderStatus}
                        onChange={(e) => handleStatusChange("orderStatus", e.target.value)}
                        label="Order Status"
                        sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                      >
                        <MenuItem value="Pending">Pending</MenuItem>
                        <MenuItem value="Completed">Completed</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  <Box
                    className="pdf-status-section"
                    data-label="Payment Status"
                    data-value={editableInvoice.paymentStatus}
                  >
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel sx={{
                        fontSize: isMobile ? '0.75rem' : '0.875rem'
                      }}>Payment Status</InputLabel>
                      <Select
                        value={editableInvoice.paymentStatus}
                        onChange={(e) => handleStatusChange("paymentStatus", e.target.value)}
                        label="Payment Status"
                        sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                      >
                        <MenuItem value="Unpaid">Unpaid</MenuItem>
                        <MenuItem value="Paid">Paid</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body1" sx={{
                    fontSize: isMobile ? '0.875rem' : '1rem'
                  }}>
                    <strong>Order Status:</strong> {editableInvoice.orderStatus}
                  </Typography>
                  <Typography variant="body1" sx={{
                    fontSize: isMobile ? '0.875rem' : '1rem'
                  }}>
                    <strong>Payment Status:</strong> {editableInvoice.paymentStatus}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Totals Box */}
            <Box sx={{
              minWidth: isMobile ? '100%' : '300px',
              padding: mfTheme.spacing.lg,
              backgroundColor: mfTheme.colors.surfaceLuxury,
              borderRadius: mfTheme.borderRadius.medium,
              border: `2px solid ${mfTheme.colors.primary}`,
              order: isMobile ? 1 : 2
            }}>
              {/* Original Total */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                <Typography variant="body1" sx={{
                  fontWeight: 500,
                  fontSize: isMobile ? '0.875rem' : '1rem'
                }}>Subtotal:</Typography>
                <Typography variant="body1" sx={{
                  fontWeight: 600,
                  fontSize: isMobile ? '0.875rem' : '1rem'
                }}>
                  £{safeToFixed(editableInvoice.totalPrice || 0)}
                </Typography>
              </Box>

              {/* Discount Display */}
              {discountType !== 'none' && discountValue && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                    <Typography variant="body1" sx={{
                      fontWeight: 500,
                      color: mfTheme.colors.warning,
                      fontSize: isMobile ? '0.875rem' : '1rem'
                    }}>
                      Discount:
                    </Typography>
                    <Typography variant="body1" sx={{
                      fontWeight: 600,
                      color: mfTheme.colors.warning,
                      fontSize: isMobile ? '0.875rem' : '1rem'
                    }}>
                      {discountType === 'percent' ? `${discountValue}%` : `-£${discountValue}`}
                    </Typography>
                  </Box>
                  <Divider sx={{ margin: '8px 0' }} />
                </>
              )}

              {/* Final Total */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" sx={{
                  fontWeight: 700,
                  color: mfTheme.colors.primary,
                  fontSize: isMobile ? '1rem' : '1.25rem'
                }}>
                  {discountType !== 'none' ? 'Discounted Total:' : 'Total:'}
                </Typography>
                <Typography variant="h6" sx={{
                  fontWeight: 700,
                  color: mfTheme.colors.primary,
                  fontSize: isMobile ? '1rem' : '1.25rem'
                }}>
                  £{safeToFixed(calculateDiscountedTotal(editableInvoice.totalPrice))}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      {/* Enhanced Dialog Actions */}
      <DialogActions sx={{
        padding: mfTheme.spacing.lg,
        backgroundColor: mfTheme.colors.surfaceElevated,
        gap: isMobile ? 1 : 2,
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        <Button
          onClick={handleClose}
          sx={{
            color: mfTheme.colors.textSecondary,
            borderRadius: mfTheme.borderRadius.medium,
            fontSize: isMobile ? '0.875rem' : '1rem',
            width: isMobile ? '100%' : 'auto'
          }}
        >
          Close
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            backgroundColor: mfTheme.colors.primary,
            borderRadius: mfTheme.borderRadius.medium,
            fontSize: isMobile ? '0.875rem' : '1rem',
            width: isMobile ? '100%' : 'auto',
            "&:hover": {
              backgroundColor: '#9E1217',
            }
          }}
        >
          Save Changes
        </Button>
        <Button
          onClick={() => setEmailDialogOpen(true)}
          variant="contained"
          startIcon={<EmailIcon />}
          sx={{
            backgroundColor: mfTheme.colors.accent,
            borderRadius: mfTheme.borderRadius.medium,
            fontSize: isMobile ? '0.875rem' : '1rem',
            width: isMobile ? '100%' : 'auto',
            "&:hover": {
              backgroundColor: '#047857',
            }
          }}
        >
          Email Invoice
        </Button>
        <Button
          onClick={handleSendInvoice}
          variant="contained"
          sx={{
            backgroundColor: mfTheme.colors.secondary,
            borderRadius: mfTheme.borderRadius.medium,
            fontSize: isMobile ? '0.875rem' : '1rem',
            width: isMobile ? '100%' : 'auto',
            "&:hover": {
              backgroundColor: '#b91c1c',
            }
          }}
        >
          Download PDF
        </Button>

        {/* Email Confirmation Dialog */}
        <Dialog
          open={emailDialogOpen}
          onClose={() => !isSendingEmail && setEmailDialogOpen(false)}
          PaperProps={{ sx: { borderRadius: 3, p: 1, minWidth: isMobile ? '90%' : '400px' } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>📧 Confirm Email Recipient</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Verify or change the email address below to send the invoice PDF.
            </Typography>
            <TextField
              fullWidth
              label="Recipient Email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              disabled={isSendingEmail}
              placeholder="email@example.com"
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setEmailDialogOpen(false)} disabled={isSendingEmail}>Cancel</Button>
            <Button
              onClick={handleSendEmail}
              variant="contained"
              disabled={isSendingEmail || !recipientEmail}
              sx={{ bgcolor: mfTheme.colors.primary, "&:hover": { bgcolor: '#9E1217' } }}
            >
              {isSendingEmail ? <CircularProgress size={24} color="inherit" /> : "Send Email"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Status Snackbar */}
        <Snackbar
          open={emailStatus.show}
          autoHideDuration={4000}
          onClose={() => setEmailStatus({ ...emailStatus, show: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={emailStatus.type} sx={{ width: '100%' }}>
            {emailStatus.message}
          </Alert>
        </Snackbar>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceModal;